import { useSyncExternalStore } from 'react'
import type { Extractor } from '@bloo-team/api-client'

/**
 * Extractor demo overlay — localStorage v1.
 *
 * Demo mode has no backend, and the extractor fixtures are a stateless module
 * constant. Writes (disable / enable / delete) therefore land in an OVERLAY
 * keyed by extractor id and applied over the fixtures on read, so a demo
 * session behaves like a tenant: toggles stick across reloads, a deleted row
 * stays gone. Live mode never touches this — the backend is the state.
 *
 * THIS SHAPE IS A DRAFT API CONTRACT (docs/extractors.md §Demo store): each
 * overlay field is the console-side echo of one captured write —
 * `status` ↔ changestatus / save STATE, `deleted` ↔ parsers/delete.
 *
 * Storage: ONE key per cluster+scope, `bloo.extractor-overlay.<clusterId>.<scopeId>`,
 * holding `{ [extractor_id]: ExtractorOverlay }`. House localStorage rules
 * (cf. hunt-store): try/catch everywhere, SSR-safe, a corrupt blob is
 * superseded by the next write rather than crashing the page.
 */

export interface ExtractorText {
  yaml: string
  samples: string[]
  at: number
}

export interface ExtractorOverlay {
  /** UPPERCASE, like the live payload. */
  status?: 'ENABLED' | 'DISABLED'
  deleted?: true
  /** Published text (the echo of `save STATE`) — what `listparserdetails` would return. */
  published?: ExtractorText
  /** Unpublished draft (the echo of `save EDIT`, id `<id>_draft`). */
  draft?: ExtractorText
  /** A row that does not exist in the fixtures — the echo of `wrk/api/parsers/add` (clone / create). */
  created?: Extractor
  /** Epoch ms of the last change — surfaces as `update_tstamp`. */
  at: number
}

export type OverlayMap = Record<string, ExtractorOverlay>

export function overlayStorageKey(clusterId: string, scopeId: string): string {
  return `bloo.extractor-overlay.${clusterId}.${scopeId}`
}

export const OVERLAY_KEY_PREFIX = 'bloo.extractor-overlay.'

export interface ExtractorOverlayStore {
  /** Stable snapshot until the next mutation. */
  all: () => OverlayMap
  setStatus: (extractorId: string, status: 'ENABLED' | 'DISABLED') => void
  markDeleted: (extractorIds: string[]) => void
  /** `save EDIT` — keep a draft beside the published text. */
  saveDraft: (extractorId: string, text: Omit<ExtractorText, 'at'>) => void
  /** `save STATE ENABLED` — the draft becomes the published text; status flips. */
  publish: (extractorId: string, text: Omit<ExtractorText, 'at'>) => void
  /** `wrk/api/parsers/add` — a new custom row with its published text. */
  create: (row: Extractor, text: Omit<ExtractorText, 'at'>) => void
  /** Drop every override — demo back to fixture state. */
  reset: () => void
  subscribe: (cb: () => void) => () => void
}

const EMPTY: OverlayMap = Object.freeze({}) as OverlayMap

class LocalOverlayStore implements ExtractorOverlayStore {
  private cache: OverlayMap | null = null
  private listeners = new Set<() => void>()
  constructor(private readonly key: string) {}

  all = (): OverlayMap => this.read()

  setStatus = (extractorId: string, status: 'ENABLED' | 'DISABLED') => {
    const cur = this.read()
    this.write({ ...cur, [extractorId]: { ...cur[extractorId], status, at: Date.now() } })
  }

  markDeleted = (extractorIds: string[]) => {
    const cur = this.read()
    const next: OverlayMap = { ...cur }
    for (const id of extractorIds) next[id] = { ...cur[id], deleted: true, at: Date.now() }
    this.write(next)
  }

  saveDraft = (extractorId: string, text: Omit<ExtractorText, 'at'>) => {
    const cur = this.read()
    const at = Date.now()
    this.write({ ...cur, [extractorId]: { ...cur[extractorId], draft: { ...text, at }, at } })
  }

  create = (row: Extractor, text: Omit<ExtractorText, 'at'>) => {
    const cur = this.read()
    const at = Date.now()
    this.write({ ...cur, [row.extractor_id]: { ...cur[row.extractor_id], created: row, published: { ...text, at }, deleted: undefined, at } })
  }

  publish = (extractorId: string, text: Omit<ExtractorText, 'at'>) => {
    const cur = this.read()
    const at = Date.now()
    const { draft: _dropped, ...rest } = cur[extractorId] ?? { at }
    this.write({ ...cur, [extractorId]: { ...rest, published: { ...text, at }, status: 'ENABLED', at } })
  }

  reset = () => {
    try {
      globalThis.localStorage?.removeItem(this.key)
    } catch {
      /* no storage: nothing to drop */
    }
    this.cache = EMPTY
    this.notify()
  }

  subscribe = (cb: () => void) => {
    this.listeners.add(cb)
    return () => {
      this.listeners.delete(cb)
    }
  }

  private read(): OverlayMap {
    if (this.cache) return this.cache
    let parsed: unknown
    try {
      const raw = globalThis.localStorage?.getItem(this.key)
      parsed = raw ? JSON.parse(raw) : {}
    } catch {
      parsed = {}
    }
    const map: OverlayMap = {}
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      for (const [id, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (!v || typeof v !== 'object') continue
        const o = v as Record<string, unknown>
        const entry: ExtractorOverlay = { at: typeof o.at === 'number' ? o.at : 0 }
        if (o.status === 'ENABLED' || o.status === 'DISABLED') entry.status = o.status
        if (o.deleted === true) entry.deleted = true
        const text = (v: unknown): ExtractorText | undefined => {
          if (!v || typeof v !== 'object') return undefined
          const t = v as Record<string, unknown>
          if (typeof t.yaml !== 'string') return undefined
          return {
            yaml: t.yaml,
            samples: Array.isArray(t.samples) ? t.samples.filter((x): x is string => typeof x === 'string') : [],
            at: typeof t.at === 'number' ? t.at : 0,
          }
        }
        const pub = text(o.published)
        if (pub) entry.published = pub
        if (o.created && typeof o.created === 'object') {
          const c = o.created as Record<string, unknown>
          if (typeof c.extractor_id === 'string' && typeof c.name === 'string') entry.created = c as unknown as Extractor
        }
        const dr = text(o.draft)
        if (dr) entry.draft = dr
        map[id] = entry
      }
    }
    this.cache = map
    return map
  }

  private write(next: OverlayMap) {
    try {
      globalThis.localStorage?.setItem(this.key, JSON.stringify(next))
    } catch {
      /* quota or no storage: keep the in-memory overlay so the UI still reflects the action */
    }
    this.cache = next
    this.notify()
  }

  private notify() {
    for (const cb of this.listeners) cb()
  }
}

const stores = new Map<string, ExtractorOverlayStore>()

/** One store per cluster+scope, shared by every hook and mutation on the page. */
export function extractorOverlayStore(clusterId: string, scopeId: string): ExtractorOverlayStore {
  const key = overlayStorageKey(clusterId, scopeId)
  let s = stores.get(key)
  if (!s) {
    s = new LocalOverlayStore(key)
    stores.set(key, s)
  }
  return s
}

/** Live view of the overlay for one scope. */
export function useExtractorOverlay(clusterId: string, scopeId: string): OverlayMap {
  const store = extractorOverlayStore(clusterId, scopeId)
  return useSyncExternalStore(store.subscribe, store.all, store.all)
}

/** Demo seeder body: forget every overlay in this browser. Idempotent. */
export function resetAllExtractorOverlays(): void {
  const ls = globalThis.localStorage
  if (!ls) return
  try {
    for (let i = ls.length - 1; i >= 0; i--) {
      const key = ls.key(i)
      if (key && key.startsWith(OVERLAY_KEY_PREFIX)) ls.removeItem(key)
    }
  } catch {
    /* no storage */
  }
  for (const s of stores.values()) s.reset()
}
